import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';

/**
 * Milestone Interface
 * Represents a milestone that can be attached to a task
 */
export interface Milestone {
  id: string;
  name: string;
  status: 'upcoming' | 'in-progress' | 'completed' | 'overdue';
  dueDate: Date | string;
  description?: string;
  startDate?: Date | string;
  endDate?: Date | string;
  progress?: number;
  taskStats?: {
    total: number;
    completed: number;
  };
  daysRemaining?: number;
  assignedTo?: string;
}

/**
 * TaskDetailsTab Component
 * Displays task information, related items (milestone), and custom fields
 *
 * Version: 2.0.0 (Phase 5 Enhancement)
 * Features:
 * - Task Information section with status, priority, deadline, and stage
 * - Related Items section with milestone attachment capability
 * - Custom Fields section for estimated hours and task stage
 */
@Component({
    selector: 'app-task-details-tab',
    templateUrl: './task-details-tab.component.html',
    styleUrls: ['./task-details-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TaskDetailsTabComponent implements OnChanges {
  // =============================================================================
  // INPUTS
  // =============================================================================

  @Input() taskForm!: FormGroup;

  // Task Information
  @Input() status: string = 'new';
  @Input() statuses: any[] = [];
  @Input() statusId: number | null = null;
  @Input() priority: string = 'medium';
  @Input() dueDate: Date | string | null = null;
  @Input() stage: string = '';
  /** Reason the task was flagged with; empty = nothing is shown. */
  @Input() flagReason: string = '';
  /** Reason the flag was removed with; empty = nothing is shown. */
  @Input() unflagReason: string = '';
  /** Edit Task page: the reason is always an editable box with Cancel / Save. Off = read-only text. */
  @Input() canEditFlagReason: boolean = false;

  /** Emits the edited reason text; the parent saves it. */
  @Output() flagReasonSave = new EventEmitter<string>();

  reasonDraft = '';

  /** True while the pencil's editable box is open in place of the reason text. */
  editingReason = false;

  /** Keeps the box in step with the saved reason (first load, after a save, after a flag / unflag). */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['flagReason'] || changes['unflagReason']) {
      this.reasonDraft = this.shownReason;
      // A saved edit, or a flag / unflag, changes the reason: the box closes and the new text shows.
      this.editingReason = false;
    }
  }

  /** The reason on display: the flag reason while flagged, the removal reason otherwise. */
  get shownReason(): string {
    return this.flagReason || this.unflagReason;
  }

  get reasonLabel(): string {
    return this.flagReason ? 'Flag Reason' : 'Unflag Reason';
  }

  /** Save is clickable whenever the box has text; a reason can be replaced but not cleared. */
  get canSaveReason(): boolean {
    return !!this.reasonDraft.trim();
  }

  /** The pencil: swaps the reason text for the editable box, starting from the saved reason. */
  startEditReason(): void {
    this.reasonDraft = this.shownReason;
    this.editingReason = true;
  }

  cancelEditReason(): void {
    this.reasonDraft = this.shownReason;
    this.editingReason = false;
  }

  saveEditReason(): void {
    const text = this.reasonDraft.trim();
    this.editingReason = false;
    // Unchanged text: nothing to send, and no duplicate History entry.
    if (!text || text === this.shownReason) { return; }
    this.flagReasonSave.emit(text);
  }

  // Related Items (Milestone)
  @Input() selectedMilestone: Milestone | null = null;

  // Custom Fields
  @Input() estimatedHours: number | null = null;
  @Input() taskStage: string = '';

  /**
   * View mode flag - makes fields read-only when true
   */
  @Input() isViewMode: boolean = false;

  /**
   * Task assignee name
   */
  @Input() assignee: string = '';

  /**
   * Task creation date
   */
  @Input() createdDate: Date | string | null = null;

  /**
   * Task description text
   */
  @Input() description: string = '';

  // =============================================================================
  // OUTPUTS
  // =============================================================================

  @Output() attachMilestone = new EventEmitter<void>();
  @Output() detachMilestone = new EventEmitter<void>();
  @Output() viewMilestone = new EventEmitter<string>();
  @Output() estimatedHoursChange = new EventEmitter<number | null>();
  @Output() taskStageChange = new EventEmitter<string>();

  // =============================================================================
  // STATUS BADGE METHODS
  // =============================================================================

  /**
   * Get status background color from API data
   * Returns the color hex code or null
   */
  getStatusColor(): string | null {
    if (this.statusId && this.statuses && this.statuses.length > 0) {
      const statusObj = this.statuses.find((s: any) => s.id === this.statusId);
      if (statusObj?.color) {
        return statusObj.color;
      }
    }

    // Fallback: match by status name — mirrors getStatusLocked()/getStatusName(),
    // and covers a terminal/locked status (e.g. Completed) whose statusId wasn't
    // passed through, or an id type mismatch (string vs number) against `statuses`.
    if (this.statuses && this.statuses.length > 0 && this.status) {
      const normalizedStatus = this.status.toLowerCase().trim();
      const statusObj = this.statuses.find((s: any) =>
        s.name?.toLowerCase().trim() === normalizedStatus
      );
      if (statusObj?.color) {
        return statusObj.color;
      }
    }

    return null;
  }

  /**
   * Check if current status is locked
   * Returns true if the status has lock_status = true
   * @returns Boolean indicating if status is locked
   */
  getStatusLocked(): boolean {
    // Try to find status in statuses array by statusId (handles both true and 1)
    if (this.statusId && this.statuses && this.statuses.length > 0) {
      const statusObj = this.statuses.find((s: any) => s.id === this.statusId);
      if (statusObj && (statusObj.lock_status === true || statusObj.lock_status === 1)) {
        return true;
      }
    }

    // Fallback: Try to find by status name
    if (this.statuses && this.statuses.length > 0 && this.status) {
      const normalizedStatus = this.status.toLowerCase().trim();
      const statusObj = this.statuses.find((s: any) =>
        s.name?.toLowerCase().trim() === normalizedStatus
      );
      if (statusObj && (statusObj.lock_status === true || statusObj.lock_status === 1)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Get current status name from statusId
   * Looks up the status name in the statuses array using the current statusId
   * @returns Status name string
   */
  getStatusName(): string {
    // Look up status by ID in statuses array
    if (this.statusId && this.statuses && this.statuses.length > 0) {
      const statusObj = this.statuses.find((s: any) => s.id === this.statusId);
      if (statusObj && statusObj.name) {
        return statusObj.name;
      }
    }

    // Fallback to status string input
    return this.status || 'new';
  }

  // =============================================================================
  // PRIORITY BADGE METHODS
  // =============================================================================

  /**
   * Get CSS class for priority badge based on current priority
   * Returns semantic class name for styling
   */
  getPriorityClass(): string {
    // Add type check to prevent runtime errors
    if (!this.priority || typeof this.priority !== 'string') {
      return 'priority-medium';
    }
    const priority = this.priority.toLowerCase();
    return `priority-${priority}`;
  }

  /**
   * Get priority color (kept for backward compatibility)
   * Note: This is replaced by CSS classes in the new implementation
   */
  getPriorityColor(): string {
    const priorityMap: { [key: string]: string } = {
      'low': '#3b82f6',      // Blue
      'medium': '#eab308',   // Yellow
      'high': '#ef4444',     // Red
      'urgent': '#9333ea'    // Purple
    };
    // Add type check to prevent runtime errors
    if (!this.priority || typeof this.priority !== 'string') {
      return '#eab308'; // Default yellow (medium priority)
    }
    return priorityMap[this.priority.toLowerCase()] || '#eab308';
  }

  // =============================================================================
  // MILESTONE STATUS METHODS
  // =============================================================================

  /**
   * Get CSS class for milestone status indicator
   * Returns semantic class name for the status dot
   */
  getMilestoneStatusClass(): string {
    if (!this.selectedMilestone) return '';
    const status = this.selectedMilestone.status;
    return `milestone-${status}`;
  }

  // =============================================================================
  // DATE FORMATTING
  // =============================================================================

  /**
   * Format date for display
   * Returns formatted date string or 'Not set' if date is null
   */
  formatDate(date: Date | string | null): string {
    if (!date) return 'Not set';

    try {
      const d = typeof date === 'string' ? new Date(date) : date;

      // Check if date is valid
      if (isNaN(d.getTime())) {
        return 'Invalid date';
      }

      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  }

  // =============================================================================
  // MILESTONE EVENT HANDLERS
  // =============================================================================

  /**
   * Handle attach milestone button click
   * Emits event to parent component to open milestone selector
   */
  onAttachMilestone(): void {
    this.attachMilestone.emit();
  }

  /**
   * Handle detach milestone button click
   * Emits event to parent component to remove milestone attachment
   */
  onDetachMilestone(): void {
    this.detachMilestone.emit();
  }

  /**
   * Handle view milestone details button click
   * Emits event to parent component to open milestone details
   */
  onViewMilestone(): void {
    if (this.selectedMilestone) {
      this.viewMilestone.emit(this.selectedMilestone.id);
    }
  }

  // =============================================================================
  // CUSTOM FIELDS EVENT HANDLERS
  // =============================================================================

  /**
   * Handle estimated hours input change
   * Emits the new value to parent component
   */
  onEstimatedHoursChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value ? parseFloat(input.value) : null;
    this.estimatedHoursChange.emit(value);
  }

  /**
   * Handle task stage select change
   * Emits the new value to parent component
   */
  onTaskStageChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.taskStageChange.emit(select.value);
  }
}
